/**
 * The VR entry point.
 *
 * `requestSession('immersive-vr')` must be called from a user gesture, so there
 * has to be something to click. This is deliberately NOT part of `src/ui`: the
 * HUD is hidden the moment a session starts, and the control that ends the
 * session cannot live inside the thing it hides.
 *
 * Styles are inline rather than in `src/ui/style.js` for the same reason — this
 * has to work whether or not the UI subsystem is even registered.
 */

const BASE = `
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 10000;
  padding: 13px 22px;
  border: 1px solid rgba(180, 205, 215, 0.42);
  border-radius: 10px;
  background: rgba(10, 14, 16, 0.78);
  color: #dbe9e5;
  font: 600 13px/1 ui-monospace, "SF Mono", Menlo, monospace;
  letter-spacing: 0.14em;
  cursor: pointer;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  transition: background 140ms ease, border-color 140ms ease, color 140ms ease;
`;

export function createVrButton({ onEnter, onExit }) {
  const el = document.createElement('button');
  el.type = 'button';
  el.id = 'ow-vr';
  el.style.cssText = BASE;
  el.textContent = 'ENTER VR';

  let presenting = false;
  let busy = false;

  const paint = () => {
    el.textContent = presenting ? 'EXIT VR' : busy ? 'STARTING…' : 'ENTER VR';
    el.style.borderColor = presenting
      ? 'rgba(224, 70, 60, 0.55)'
      : 'rgba(180, 205, 215, 0.42)';
    el.disabled = busy;
    el.style.opacity = busy ? '0.6' : '1';
  };

  const onClick = async (e) => {
    // Pointer lock and the canvas click handler both want this event; a VR
    // session request that also locks the cursor leaves a captured pointer
    // behind when the headset takes over.
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    busy = true;
    paint();
    try {
      if (presenting) await onExit();
      else await onEnter();
    } finally {
      busy = false;
      paint();
    }
  };

  el.addEventListener('click', onClick);
  el.addEventListener('mouseenter', () => {
    el.style.background = 'rgba(24, 32, 36, 0.9)';
  });
  el.addEventListener('mouseleave', () => {
    el.style.background = 'rgba(10, 14, 16, 0.78)';
  });
  document.body.appendChild(el);

  return {
    element: el,
    setPresenting(v) {
      presenting = v;
      paint();
    },
    setError(msg) {
      el.textContent = String(msg).toUpperCase();
      el.style.borderColor = 'rgba(224, 70, 60, 0.55)';
      setTimeout(paint, 2600);
    },
    dispose() {
      el.removeEventListener('click', onClick);
      el.remove();
    },
  };
}
